import { Badge, Button, Col, Input, Popconfirm, Row, Space, Table, Tabs, message } from 'antd';
import { PageHeader } from "@ant-design/pro-layout";
import { DeleteFilled } from "@ant-design/icons";
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { marketplaces, unitTestType } from 'helpers';
import { addUnitTestNote, deleteUnitTestNote, getUniteTestNote } from 'services';
import { getIdFromObject } from 'utils';
import { Helmet } from 'react-helmet';
const { TabPane } = Tabs;

interface TCategory {
  _id: string;
  name: string | null;
  unitTest: string;
  note: string | null;
  numOfASINs: number;
  passed: boolean;
  marketplace: string;
}

const initCategory = {
  name: null,
  unitTest: unitTestType.REFERRAL,
  note: null,
  numOfASINs: 0,
  passed: false,
  marketplace: "US"
}

const ReferralFee = () => {
  const [activedTab, setActivedTab] = useState<string>("US");
  const [dataTable, setDataTable] = useState<any>([]);
  const [newCategory, setNewCategory] = useState<any>(initCategory);

  let { marketplace = "US" } = useParams();
  let navigate = useNavigate();

  // To disable submit button at the beginning.
  useEffect(() => {
    getUniteTestNote(unitTestType.REFERRAL, marketplace)
      .then(({ data }) => {
        const categories = (data.data && Array.isArray(data.data)) ? data.data : []
        setDataTable(categories)
      })
      .catch(() =>
        message.error("Cannot get categories, please refresh page again")
      )
  }, [activedTab])

  useEffect(() => {
    setActivedTab(marketplace || "US")
  }, [marketplace]);

  const handleOnChangeTab = (tabKey: string) => {
    navigate(`../${tabKey}`, { replace: true });
  };

  const handleOnDelete = (item: any) => {
    const id = typeof item._id === 'string' ? item._id : getIdFromObject(item)
    return deleteUnitTestNote(id)
      .then(() => {
        const newData = dataTable.filter((d: any) => {
          const dId = typeof d._id === 'string' ? d._id : getIdFromObject(d)
          return dId !== id
        });
        setDataTable(newData);
        message.success("Deleted category successfully")
      })
      .catch(() => message.error("Cannot delete category"))
  }

  const handleOnSaveNewCategory = () => {
    return addUnitTestNote(unitTestType.REFERRAL, marketplace, newCategory)
      .then(({ data }) => {
        setDataTable((prev: any) => {
          return [data.data, ...prev]
        });
      })
      .catch(() => message.error("Cannot add category"))
  }

  const columns: ColumnsType<TCategory> = [
    {
      title: 'Category name',
      dataIndex: 'categoryName',
      key: 'categoryName',
      render: (text, record) => {
        const id = typeof record._id === 'string' ? record._id : getIdFromObject(record)
        return <Link to={id}>{text}</Link>
      },
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      width: 500
    },
    {
      title: '# ASINs',
      dataIndex: 'numOfASINs',
      key: 'numOfASINs',
    },
    {
      title: 'Passed',
      dataIndex: 'passed',
      key: 'passed',
      render: value => (
        <Badge
          text={value ? "Passed" : "Not Passed"}
          status={value ? "success" : "error"}
        />
      )
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => {
        return (
          <Space size="middle">
            <Button type="primary">
              <Link to={typeof record._id === 'string' ? record._id : getIdFromObject(record)}>Edit</Link>
            </Button>
            <Popconfirm
              placement="topRight"
              title="Are you sure to delete this category?"
              onConfirm={() => handleOnDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" icon={<DeleteFilled />} danger />
            </Popconfirm>
          </Space>
        )
      },
    },
  ];

  return (
    <>
      <Helmet>
        <title>Unit Test | Referral Fee</title>
      </Helmet>
      <PageHeader
        className="page-header"
        backIcon={true}
        title="Unit Test for Referral Fee"
        footer={
          <Tabs defaultActiveKey={activedTab} type="card" activeKey={activedTab} onChange={handleOnChangeTab}>
            {marketplaces.map(m => (
              <TabPane tab={m.label} key={m.key}>
                <Space direction="vertical" size="large" style={{ width: '100%', marginTop: 30 }}>
                  <Input.Group>
                    <Row gutter={8}>
                      <Col span={4}>
                        <label>Category name:</label>
                      </Col>
                      <Col span={10}>
                        <label>Note:</label>
                      </Col>
                    </Row>
                    <Row gutter={8}>
                      <Col span={4}>
                        <Input
                          value={newCategory.name}
                          onChange={(e) =>
                            setNewCategory((prev: any) =>
                            ({
                              ...prev,
                              name: e.target.value,
                            })
                            )
                          }
                        />
                      </Col>
                      <Col span={10}>
                        <Input.TextArea
                          value={newCategory.note}
                          onChange={(e) =>
                            setNewCategory((prev: any) =>
                            ({
                              ...prev,
                              note: e.target.value,
                            })
                            )
                          }
                        />
                      </Col>
                      <Col span={4} offset={1}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          onClick={handleOnSaveNewCategory}
                        >
                          Save
                        </Button>
                      </Col>
                    </Row>
                  </Input.Group>
                  <Table columns={columns} dataSource={dataTable} scroll={{ x: true }} />
                </Space>
              </TabPane>
            ))}
          </Tabs>
        }
      />
    </>
  )
};

export default ReferralFee;